import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Row, Col, Spinner, Input, Badge,
} from "reactstrap";
import { Link } from "react-router-dom";
import useMyProperties from "../../../hooks/useMyProperties";
import PageHero from "../../../components/Common/PageHero";
import PropertyChat from "../../../components/Common/PropertyChat";

/**
 * One inbox for every property conversation. Pick a property on the left and
 * the shared group chat opens on the right — same thread as the property page.
 */
const ChatsPage = () => {
  document.title = "Chats | Support Portal";

  const { user, properties, loading } = useMyProperties();
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!selectedId && properties.length > 0) setSelectedId(properties[0].id);
  }, [properties, selectedId]);

  const term = search.trim().toLowerCase();
  const visible = term
    ? properties.filter((p) => (p.address || "").toLowerCase().includes(term))
    : properties;

  const selected = properties.find((p) => p.id === selectedId) || null;

  return (
    <div className="page-content">
      <Container fluid>
        <PageHero
          title="Chats"
          subtitle="Message the tenants and owners on any of your properties without leaving this page."
        />

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : properties.length === 0 ? (
          <Card>
            <CardBody className="text-center py-5">
              <i className="bx bx-conversation text-muted" style={{ fontSize: "2.5rem" }} />
              <h5 className="text-muted mt-2">No properties yet</h5>
              <p className="text-muted mb-0">
                Add a property to start a conversation with its tenants.
              </p>
            </CardBody>
          </Card>
        ) : (
          <Row className="g-3">
            <Col md={4} lg={3}>
              <Card className="h-100">
                <CardBody className="p-2">
                  <Input
                    bsSize="sm"
                    className="mb-2"
                    placeholder="Search properties…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <div className="list-group list-group-flush" style={{ maxHeight: 520, overflowY: "auto" }}>
                    {visible.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={`list-group-item list-group-item-action text-start ${p.id === selectedId ? "active" : ""}`}
                        onClick={() => setSelectedId(p.id)}
                      >
                        <div className="text-truncate fw-semibold">{p.address}</div>
                        <small className={p.id === selectedId ? "text-white-50" : "text-muted"}>
                          {(p.tenantEmails || []).length} tenant
                          {(p.tenantEmails || []).length === 1 ? "" : "s"}
                        </small>
                      </button>
                    ))}
                    {visible.length === 0 && (
                      <p className="text-muted small fst-italic p-2 mb-0">No matches.</p>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col md={8} lg={9}>
              <Card className="h-100">
                <CardBody>
                  {selected ? (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="text-truncate">
                          <h5 className="mb-0 text-truncate">{selected.address}</h5>
                          <small className="text-muted">
                            {(selected.tenantEmails || []).length} tenant
                            {(selected.tenantEmails || []).length === 1 ? "" : "s"}
                            {(selected.sharedEmails || []).length > 0 && (
                              <Badge color="light" className="text-dark ms-2 fw-normal">
                                shared with {(selected.sharedEmails || []).length}
                              </Badge>
                            )}
                          </small>
                        </div>
                        <Link
                          to={`/properties/${selected.id}`}
                          className="btn btn-sm btn-outline-primary flex-shrink-0"
                        >
                          <i className="bx bx-building-house me-1" />
                          Open Property
                        </Link>
                      </div>
                      <PropertyChat
                        key={selected.id}
                        propertyId={selected.id}
                        user={user}
                        height={420}
                        placeholder={`Message everyone at ${selected.address}…`}
                      />
                    </>
                  ) : (
                    <p className="text-muted small fst-italic mb-0">
                      Pick a property to open its conversation.
                    </p>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ChatsPage;
